import { Empty, Popover, Spin, Typography } from 'antd'
import { useState } from 'react'
import { FaCheck } from 'react-icons/fa'

import { serverMessage } from '@/Ship/Helpers/serverError'

import { APPLICATION_REQUIREMENT_LABELS, APPLICATION_TEXTS as T } from '../Contracts/ApplicationFieldLabels'
import { LoadStatusRequirementsTask } from '../Tasks/LoadStatusRequirementsTask'

import type { ReactElement } from 'react'

const { Text } = Typography

interface Props {
    uuid: string
    code: string
    title: string
    /** Сам крок смуги — кнопка, на клік по якій відкривається вікно. */
    children: ReactElement
}

type State = { kind: 'loading' } | { kind: 'failed'; message: string } | { kind: 'loaded'; labels: string[] }

/**
 * «Чого бракує до статусу» — невеликим вікном біля кроку смуги (`SPEC-021`
 * §8.4, ДЗ-08), за зразком вікна залишків номенклатури на складі.
 *
 * Раніше клік переводив усю картку в редагування й підсвічував поля, як у
 * легасі: картка перевідкривалася, а людина, яка хотіла лише подивитися,
 * опинялася у формі. Тепер — лише перелік; редагують картку звичайною
 * кнопкою «Редагувати».
 *
 * Перечитуємо на кожне відкриття: заявку могли щойно змінити, а бек рахує
 * вимоги від її поточного стану.
 */
export const ApplicationStepRequirements = ({ uuid, code, title, children }: Props) => {
    const [state, setState] = useState<State>({ kind: 'loading' })

    const load = async (): Promise<void> => {
        setState({ kind: 'loading' })

        try {
            const result = await LoadStatusRequirementsTask(uuid, code)
            // Кілька вимог одного поля (наприклад, «вказати» й «формат») —
            // для людини це один пункт.
            const labels = [
                ...new Set(
                    result.violations.map(
                        ({ propertyPath, message }) => (propertyPath ? APPLICATION_REQUIREMENT_LABELS[propertyPath] : undefined) ?? message
                    )
                ),
            ]

            setState({ kind: 'loaded', labels: result.satisfied ? [] : labels })
        } catch (error: unknown) {
            setState({ kind: 'failed', message: serverMessage(error, T.contactAdmin) })
        }
    }

    const content = (): React.ReactNode => {
        if (state.kind === 'loading') return <Spin size="small" />
        if (state.kind === 'failed') return <Text type="danger">{state.message}</Text>

        if (state.labels.length === 0) {
            return (
                <Empty
                    image={<FaCheck style={{ fontSize: 24, color: 'var(--ant-color-success)' }} />}
                    imageStyle={{ height: 28 }}
                    description={T.requirementsSatisfied(title)}
                />
            )
        }

        return (
            <ul style={{ margin: 0, paddingInlineStart: 18, maxWidth: 320 }}>
                {state.labels.map((label) => (
                    <li key={label}>{label}</li>
                ))}
            </ul>
        )
    }

    return (
        <Popover
            trigger="click"
            placement="bottomLeft"
            title={state.kind === 'loaded' && state.labels.length === 0 ? null : T.requirementsTitle(title)}
            content={content()}
            onOpenChange={(open) => {
                if (open) void load()
            }}
        >
            {children}
        </Popover>
    )
}
